import React from "react";
import { ChangeEvent } from "react";
import { Link, Prompt, RouteComponentProps } from "react-router-dom";

import { apiGet, apiPatch, apiPost, V2 } from "../../Api";
import withTitle from "../withTitle";
import ExpandList, { ExpandListEvent } from "../ExpandList";
import { formatName, formatTelephone } from "../../Utilities";

import HasRight from "../HasRight";
// import Indicator from "../Indicator";
import TagListInput from "../TagListInput";
// import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
// import "../../icomoon.css";
// import "../../indicator.css";
// import "../../list.css";
import "../../spinner.css";
// import "../../table.css";
import "../../user.css";
import "../viewer.css";

interface IEmployeeDetailsState {
	error?: string;
	isLoading: boolean;
	employee?: any;

	tags: any[];
	tagOptions: any[];
	groups: any[];
	groupList: any[];
	underlings: any[];
	assessmentTypes: any[];
	hasUnderlingsChanged: boolean;
	hasAssessmentTypeChanged: boolean;

	allEmployees: any[];

	firstName: string;
	lastName: string;
	suffix: string;
	title: string;

	phone: string;
	email: string;
	assessmentId: string;

	vehiclePin: string;

	location: string;
	locations: any[];

	hasChanged: boolean;
	isSubmitting: boolean;
	submitError?: string;
}

class EmployeeDetailsView extends React.Component<RouteComponentProps<any>, IEmployeeDetailsState> {
	constructor(props: Readonly<RouteComponentProps<any>>) {
		super(props);

		this.state = {
			isLoading: true,

			tagOptions: [],
			tags: [],
			groups: [],
			groupList: [],
			underlings: [],
			assessmentTypes: [],
			hasUnderlingsChanged: false,
			hasAssessmentTypeChanged: false,

			allEmployees: [],

			firstName: "",
			lastName: "",
			suffix: "",
			title: "",

			phone: "",
			email: "",
			assessmentId: "",

			vehiclePin: "",

			location: "",
			locations: [],

			hasChanged: false,
			isSubmitting: false,
		};

		this.handleRolesChange = this.handleRolesChange.bind(this);
		this.submitChanges = this.submitChanges.bind(this);
		this.cancelChanges = this.cancelChanges.bind(this);
		this.handleInputChanged = this.handleInputChanged.bind(this);
		this.addGroup = this.addGroup.bind(this);
		this.deleteGroup = this.deleteGroup.bind(this);
		this.addUnderling = this.addUnderling.bind(this);
		this.deleteUnderling = this.deleteUnderling.bind(this);
	}

	public async componentDidMount() {
		const userId = this.props.match && this.props.match.params.id;

		try {
			// Load locations
			const [locations, groupList, assessmentTypes] = await Promise.all([
				apiGet<any[]>("locations"),
				apiGet<any[]>("employees/groups"),
				V2.get<any[]>("metrics/assessments/types"),
			]);

			this.setState({
				locations,
				groupList,
				assessmentTypes,
			});

			if (locations.length < 1) {
				this.setState({
					error: "You must create at least one office location first",
				});
				return;
			}

			if (userId) {
				// Load existing employee
				const [employee, underlings, allEmployees, assessmentType] = await Promise.all([
					apiGet<any>(`employees/${userId}`),
					V2.get<any[]>(`employees/${userId}/underlings`),
					apiGet<any[]>(`employees/summary?limit=1500`),
					V2.get<any>(`employees/${userId}/assessment-type`),
				]);

				this.setState({
					employee,
					isLoading: false,

					tags: employee.tags,
					groups: employee.groups,
					underlings: underlings,

					allEmployees: allEmployees.sort((a: any, b: any) =>
						formatName(a.name).localeCompare(formatName(b.name))
					),

					firstName: employee.name.first,
					lastName: employee.name.last,
					suffix: employee.name.suffix,
					title: employee.name.title,

					phone: formatTelephone(employee.phone),
					email: employee.email,
					assessmentId: assessmentType.id || "",

					location: employee.locationId || locations[0].id,

					vehiclePin: employee.vehicle ? employee.vehicle.pin : "",
				});
			} else {
				this.setState({
					isLoading: false,

					location: locations[0].id,
				});
			}
		} catch (e) {
			this.setState({
				error: e.message,
				isLoading: false,
			});
		}
	}

	public render() {
		if (this.state.isLoading) {
			return (
				<div className="viewer-root">
					<div className="spinner-center lds-dual-ring-big" />
				</div>
			);
		}

		if (this.state.error) {
			return (
				<div className="viewer-root">
					<span className="error-text">{this.state.error}</span>
				</div>
			);
		}

		return (
			<div className="viewer-root with-actionbar">
				<Prompt
					when={this.state.hasChanged}
					message="Your changes will not be saved if you continue."
				/>
				<div className="column-root input-form">
					<section className="info-block">
						<header>
							<h3>Employee Details</h3>
						</header>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-first-name">First Name</label>
								<input
									type="text"
									className="input-light"
									id="frm-first-name"
									name="firstName"
									value={this.state.firstName}
									onChange={this.handleInputChanged}
								/>
							</div>
							<div className="input-form-group">
								<label htmlFor="frm-last-name">Last Name</label>
								<input
									type="text"
									className="input-light"
									id="frm-last-name"
									name="lastName"
									value={this.state.lastName}
									onChange={this.handleInputChanged}
								/>
							</div>
						</section>
						<section>
							<div className="input-form-group">
								<label htmlFor="frm-title">Title</label>
								<input
									type="text"
									className="input-light"
									id="frm-title"
									name="title"
									value={this.state.title}
									onChange={this.handleInputChanged}
									placeholder="Dr., Sir., etc."
								/>
							</div>
							<div className="input-form-group">
								<label htmlFor="frm-suffix">Suffix</label>
								<input
									type="text"
									className="input-light"
									id="frm-suffix"
									name="suffix"
									value={this.state.suffix}
									onChange={this.handleInputChanged}
								/>
							</div>
						</section>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-location">Primary Office Location</label>
								<select
									className="input-light"
									value={this.state.location}
									id="frm-location"
									name="location"
									onChange={this.handleInputChanged}>
									{this.state.locations.map((a) => (
										<option
											key={a.id}
											value={a.id}>
											{a.name}
										</option>
									))}
								</select>
							</div>
							<div className="input-form-group">
								<label htmlFor="frm-phone">Work Phone #</label>
								<input
									type="text"
									className="input-light"
									id="frm-phone"
									name="phone"
									value={this.state.phone}
									onChange={this.handleInputChanged}
								/>
							</div>
						</section>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-email">Email Address</label>
								<input
									type="text"
									className="input-light"
									id="frm-email"
									name="email"
									value={this.state.email}
									onChange={this.handleInputChanged}
								/>
							</div>
						</section>

						{this.state.employee ? (
							<section>
								<div className="input-form-group">
									<label>User Account</label>

									{this.state.employee && this.state.employee.userId ? (
										<Link to={`/users/${this.state.employee.userId}`}>View</Link>
									) : (
										<div>
											{/*Link <span className="small-text">(not implemented)</span>*/}{" "}
											<HasRight right="user:create">
												<Link
													to={{ pathname: "/users/add", state: { employee: this.state.employee } }}>
													Create account
												</Link>
											</HasRight>
										</div>
									)}
								</div>
							</section>
						) : (
							""
						)}
					</section>
					<section className="info-block">
						<header>
							<h3>Groups &amp; Tags</h3>
						</header>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-groups">Group membership</label>
								<ExpandList
									items={this.state.groups}
									itemRender={(a) => a.name}
									itemIdField="id"
									onAdd={this.addGroup}
									onDelete={this.deleteGroup}
									autoAdd={true}
									options={this.state.groupList}
									optionOuterRender={(a) => (
										<option
											key={a.id}
											value={a.id}
											disabled={!!this.state.groups.find((b) => b.id === a.id)}>
											{a.name}
										</option>
									)}
								/>
							</div>
						</section>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-tags">Tags</label>
								<TagListInput
									id="frm-tags"
									items={this.state.tags}
									requestOptions={this.populateTags}
									allowCustom={true}
									onItemsChange={this.handleRolesChange}
									placeholder="Enter tags..."
								/>
								<small>
									<br />
									<em>Remember to press enter after typing in a tag</em>
								</small>
							</div>
						</section>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-assessment-id">Assessment Type</label>
								<select
									className="input-light"
									value={this.state.assessmentId}
									id="frm-assessment-id"
									name="assessmentId"
									onChange={this.handleInputChanged}>
									<option value="">None</option>
									{this.state.assessmentTypes.map((a) => (
										<option
											key={a.id}
											value={a.id}>
											{a.name}
										</option>
									))}
								</select>
							</div>
						</section>
					</section>
				</div>
				&nbsp;
				<div className="column-root input-form">
					<section className="info-block">
						<header>
							<h3>Subordinates</h3>
						</header>

						<p>
							<small>
								If this employee is a manager, you may specify below who they are directly over.
							</small>
						</p>

						<ExpandList
							items={this.state.underlings}
							itemRender={(a) => formatName(a.name)}
							itemIdField="id"
							onAdd={this.addUnderling}
							onDelete={this.deleteUnderling}
							autoAdd={true}
							options={this.state.allEmployees}
							optionOuterRender={(a) => (
								<option
									key={a.id}
									value={a.id}>
									{formatName(a.name)}
								</option>
							)}
						/>
					</section>
					<section className="info-block">
						<header>
							<h3>Vehicles</h3>
						</header>

						<section>
							<div className="input-form-group">
								<label htmlFor="frm-vehicle-pin">Vehicle PIN</label>
								<input
									type="text"
									className="input"
									id="frm-vehicle-pin"
									name="vehiclePin"
									value={this.state.vehiclePin}
									maxLength={8}
									onChange={this.handleInputChanged}
								/>
							</div>
							<div className="input-form-group" />
						</section>
						<section>
							<div className="input-form-group">
								<span className="small-text">Maximum 8 digits</span>
								<span className="small-text">Leave PIN blank to disallow use of the kiosk</span>
								<span className="small-text">
									Enter a single dash (-) for a randomly-generated PIN
								</span>
							</div>
						</section>
					</section>
				</div>
				&nbsp;
				{this.state.submitError ? <span className="error-text">{this.state.submitError}</span> : ""}
				<div className="action-panel">
					<button
						className="button blue btn-confirm push-right"
						disabled={this.state.isSubmitting}
						onClick={this.submitChanges}>
						Save
					</button>
					<button
						className="button blue btn-cancel"
						disabled={this.state.isSubmitting}
						onClick={this.cancelChanges}>
						Cancel
					</button>
				</div>
			</div>
		);
	}

	private handleRolesChange(items: any[]) {
		this.setState({
			hasChanged: true,
			tags: items,
		});
	}

	private async submitChanges() {
		try {
			if (this.state.employee) {
				// Update existing user
				this.setState({
					isSubmitting: true,
					submitError: undefined,
				});

				const update = {
					name: {
						first: this.state.firstName,
						last: this.state.lastName,
						suffix: this.state.suffix,
						title: this.state.title,
					},
					tags: this.state.tags,
					groups: this.state.groups.map((a) => ({ id: a.id })),

					locationId: this.state.location || null,
					phone: (this.state.phone || "")?.replace(/[^\d]/g, ""),
					email: this.state.email || "",

					vehicle: {
						pin: this.state.vehiclePin,
					},
				};

				await apiPatch(`employees/${this.state.employee.id}`, update);
				if (this.state.hasUnderlingsChanged) {
					const underlingData = {
						ids: this.state.underlings.map((a) => a.id),
					};
					await V2.put<void>(`employees/${this.state.employee.id}/underlings`, underlingData);
				}
				if (this.state.hasAssessmentTypeChanged) {
					const assessmentData = {
						id: this.state.assessmentId,
					};
					await V2.put<void>(`employees/${this.state.employee.id}/assessment-type`, assessmentData);
				}

				this.setState({
					hasChanged: false,
					isSubmitting: false,
				});
			} else {
				// Create new employee
				const creationInfo = {
					name: {
						first: this.state.firstName,
						last: this.state.lastName,
						suffix: this.state.suffix,
						title: this.state.title,
					},
					tags: this.state.tags,
					groups: this.state.groups.map((a) => ({ id: a.id })),

					locationId: this.state.location || null,

					phone: (this.state.phone || null)?.replace(/[^\d]/g, ""),
					email: this.state.email || null,

					vehicle: {
						pin: this.state.vehiclePin,
					},
				};

				this.setState({
					isSubmitting: true,
					submitError: undefined,
				});

				await apiPost("employees", creationInfo);

				this.setState({
					hasChanged: false,
					isSubmitting: false,
				});
			}
			this.props.history.goBack();
		} catch (e) {
			this.setState({
				isSubmitting: false,
				submitError: e.message,
			});
		}
	}

	private cancelChanges() {
		this.props.history.goBack();
	}

	private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
		const value = e.currentTarget.value;
		const name = e.currentTarget.name;
		const hasAssessmentTypeChanged = this.state.hasAssessmentTypeChanged || name === "assessmentId";

		this.setState({
			hasChanged: true,
			hasAssessmentTypeChanged,
			[name]: value,
		} as any);
	}

	private populateTags(search: string) {
		search = search.toLowerCase();
		return [
			"marketer",
			"np",
			"prac",
			"prac_gyn",
			"dr",
			"circulating",
			"scrub",
			"anesthesia",
			":hide",
		].filter((a) => a.toLowerCase().indexOf(search) > -1);
	}

	private addGroup(e: ExpandListEvent<any>) {
		const groups = this.state.groups;

		groups.push({
			id: e.item.id,
			name: e.item.name,
		});

		this.setState({
			groups,
		});
	}

	private deleteGroup(e: ExpandListEvent<any>) {
		if (e.index === undefined) {
			return;
		}

		const groups = this.state.groups;

		this.state.groups.splice(e.index, 1);

		this.setState({
			groups,
		});
	}

	private addUnderling(e: ExpandListEvent<any>) {
		const underlings = this.state.underlings;

		underlings.push({
			id: e.item.id,
			name: e.item.name,
		});

		this.setState({
			hasUnderlingsChanged: true,
			underlings,
		});
	}

	private deleteUnderling(e: ExpandListEvent<any>) {
		if (e.index === undefined) {
			return;
		}

		const underlings = this.state.underlings;

		this.state.underlings.splice(e.index, 1);

		this.setState({
			hasUnderlingsChanged: true,
			underlings,
		});
	}
}

export default withTitle("Employee Details", EmployeeDetailsView);
